import { Store } from "pullstate";
import { IdealCustomerProfile } from "../types/IdealCustomerProfile";
import { FormError } from "../../../../types/FormError";
import { Filter, FilterType, OptionWithSelect } from "../../../filters/FilterTypes";
import { InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Pagination } from "../../../../types/common";
import { LeadGridItem } from "../../../../types/Lead";
import { client } from "../../../../utils/axios-client";
import { GridLinkOperator } from "@mui/x-data-grid-pro";
import isEmpty from "lodash/isEmpty";
import { IdealCustomerProfileListItem, Sprint } from "../../../../stores/SprintStore";


const now = new Date();

export const icpTemplate = {
    id: crypto.randomUUID(),
    sprintId: '',
    name: '',
    cadenceId: '',
    cadenceStartDate: now.toISOString(),
    ownerId: '',
    primaryFilters: {
        id: crypto.randomUUID(),
        sprintId: '',
        name: '',
        description: '',
        searchTerm: '',
        options: [],
        activityDate: 'any',
        filterType: FilterType.ICP
    } as Filter,
    refinedFilters: [],
    refinedFiltersLinkOperator: GridLinkOperator.And
} as IdealCustomerProfile


//export const icpTemplate2 = {
//    id: crypto.randomUUID(),
//    sprintId: '',
//    name: '',
//    cadenceId: '',
//    cadenceStartDate: now.toISOString(),
//    ownerId: '',
//    "primaryFilters": {
//        "id": "63019849-fa48-42a5-b9bf-21d597f1da40",
//        "description": "",
//        "searchTerm": "",
//        "options": [
//            {
//                "id": "e87c02eb-645a-4c6d-9ea9-c05483a50069",
//                "parentId": "",
//                "value": "2838f9c2-ae70-463b-8db1-764cb3883f1e",
//                "name": "British Airways",
//                "optionType": "ACCOUNT",
//                "selected": true,
//                "inclusive": true
//            }
//        ],
//        "activityDate": "any",
//        "filterType": 2
//    },
//    refinedFilters: [],
//    refinedFiltersLinkOperator: GridLinkOperator.And
//} as IdealCustomerProfile


const ICPStore = new Store({
    step: 0,
    sprint: {
        idealCustomerProfiles: [] as IdealCustomerProfileListItem[]
    } as Sprint,
    icp: structuredClone(icpTemplate),
    errors: [] as FormError[],
})

export const useSaveIcp = () => {
    return useMutation(
        (icp: IdealCustomerProfile) => client.post<boolean>('/api/sprints/icp', icp).then((res) => res.data),
        {
            onSuccess: () => {                
            }
        })
}



const useICPWizard = () => {
    const icp = ICPStore.useState(s => s.icp);
    const step = ICPStore.useState(s => s.step);
    const sprint = ICPStore.useState(s => s.sprint);
    const errors = ICPStore.useState(s => s.errors);
    const { mutateAsync: saveICP } = useSaveIcp();
    const queryClient = useQueryClient();

    const createIcp = (selectedSprint: Sprint) => {
        ICPStore.update(s => {
            s.step = 0;
            s.sprint = selectedSprint
            const newIcp = structuredClone(icpTemplate);
            newIcp.id = crypto.randomUUID();
            newIcp.sprintId = selectedSprint.id;
            s.icp = newIcp;
        })
    }

    const handleNextStep = () => {
        if (icpIsValid()) {
            ICPStore.update(s => {
                s.step++;
            });
        }
    }

    const handlePreviousStep = () => {
        ICPStore.update(s => {
            s.step--;
        });
    }

    const icpIsValid = () => {
        const temp = [] as FormError[];

        if (step === 3) {
            if (isEmpty(icp.name)) temp.push({ message: 'You must specify a name for the profile', propertyName: 'name', recordId: '' });
            if (isEmpty(icp.cadenceId)) temp.push({ message: 'You must specify a cadence', propertyName: 'cadenceId', recordId: '' });
            if (isEmpty(icp.cadenceStartDate)) temp.push({ message: 'You must specify a start date', propertyName: 'cadenceStartDate', recordId: '' });
        }        

        ICPStore.update(s => {
            s.errors = temp;
        })

        return temp.length === 0;
    }

    const onICPChange = (newValue: IdealCustomerProfile) => {
        ICPStore.update(s => {
            s.icp = structuredClone(newValue);
        });
    }

    const resetLeads = (options: OptionWithSelect[], searchTerm: string, pageSize: number, unassignedOnly: boolean, selectedSprintId: string) => {
        //queryClient.removeQueries(["icp-leads", searchTerm, unassignedOnly, selectedSprintId, options]);
        queryClient.setQueryData(["icp-leads", searchTerm, unassignedOnly, selectedSprintId, options], (data: any) => ({
            pages: data.pages.slice(0, 1),
            pageParams: data.pageParams.slice(0, 1),
        }))
    }

    const useGetIcpLeads = (options: OptionWithSelect[], searchTerm: string, pageSize: number, unassignedOnly: boolean, selectedSprintId: string) => useInfiniteQuery<Pagination<LeadGridItem>>({
        queryKey: ["icp-leads", searchTerm, unassignedOnly, selectedSprintId, options],
        queryFn: ({ pageParam = 1 }) => {
            return client.post(`/api/leads/filter/icp`, {
                options: options,
                searchTerm: searchTerm,
                pageSize: pageSize,
                pageNumber: pageParam,
                unassignedOnly: unassignedOnly,
                selectedSprintId: selectedSprintId,
                refinedFilters: icp.refinedFilters,
                refinedFiltersLinkOperator: icp.refinedFiltersLinkOperator

            }).then((res) => res.data)
        },
        getNextPageParam: (lastPage, pages) => {
            if (lastPage.hasNextPage) return lastPage.pageNumber + 1;
            return false;
        }
    })

    const loadIcp = async (icpId: string) => {
        const result = await client.get(`/api/sprints/icp/${icpId}`);
        ICPStore.update(s => {            
            s.icp = result.data;
            s.step = step + 1;
        });
    }

    const deleteIcp = async (icpId: string) => {
        const result = await client.delete(`/api/sprints/icp/${icpId}`);        

        const temp = structuredClone(sprint);
        const index = temp.idealCustomerProfiles.findIndex(s => s.id === icpId);
        temp.idealCustomerProfiles.splice(index, 1);

        ICPStore.update(s => {
            s.sprint = temp;
        });
    }

    const reset = () => {
        createIcp(sprint);
    }

    return {
        icp,
        onICPChange,
        createIcp,
        loadIcp,
        deleteIcp,
        icpIsValid,
        errors,
        useGetIcpLeads,
        resetLeads,
        step,
        sprint,
        saveICP,
        handleNextStep,
        handlePreviousStep,
        reset
    }
}

export default useICPWizard;